import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Gift, Copy, Check, Users, Coins, Share2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralSection = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const referralLink = `${window.location.origin}/register?ref=${user?.referral_code}`;

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Show the text in a prompt as last resort
      window.prompt('Copia questo codice:', text);
    }
  };

  const shareOnTwitter = () => {
    const text = `Ottimizza il SEO del tuo sito con AEO Orchestra! Usa il mio codice ${user?.referral_code} per un 20% di sconto.`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  if (!user) return null;

  return (
    <div className="bg-[#111111] border border-white/10 rounded-2xl p-6" data-testid="referral-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
          <Gift className="w-6 h-6 text-[#F59E0B]" />
        </div>
        <div>
          <h3 className="font-['Outfit'] font-medium text-lg">Programma Referral</h3>
          <p className="text-sm text-[#A1A1AA]">Guadagna crediti invitando amici</p>
        </div>
      </div>

      {/* Referral Code */}
      <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6">
        <span className="text-xs text-[#A1A1AA] block mb-2">Il tuo codice referral</span>
        <div className="flex items-center justify-between gap-4">
          <code className="font-mono text-xl text-[#F59E0B]">{user.referral_code}</code>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => copyToClipboard(user.referral_code)}
            className="text-[#A1A1AA] hover:text-white"
            data-testid="copy-referral-code"
          >
            {copied ? <Check className="w-5 h-5 text-[#10B981]" /> : <Copy className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Referral Link */}
      <div className="mb-6">
        <span className="text-xs text-[#A1A1AA] block mb-2">Link di invito</span>
        <div className="flex gap-2">
          <Input 
            value={referralLink} 
            readOnly 
            className="input-field text-sm flex-1"
          />
          <Button
            onClick={() => copyToClipboard(referralLink)}
            className="bg-[#0055FF] hover:bg-[#3377FF] px-4"
            data-testid="copy-referral-link"
          >
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Share Buttons */}
      <div className="flex gap-2 mb-6">
        <Button
          onClick={shareOnTwitter}
          variant="outline"
          className="flex-1 border-white/10 hover:bg-white/5"
        >
          <Share2 className="w-4 h-4 mr-2" />
          Twitter
        </Button>
        <Button
          onClick={shareOnLinkedIn}
          variant="outline"
          className="flex-1 border-white/10 hover:bg-white/5"
        >
          <Share2 className="w-4 h-4 mr-2" />
          LinkedIn
        </Button>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="text-center py-4">
          <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Users className="w-6 h-6 text-[#0055FF] mx-auto mb-2" />
            <div className="text-2xl font-['Outfit'] font-semibold">{stats.total_referrals}</div>
            <div className="text-xs text-[#A1A1AA]">Invitati</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Coins className="w-6 h-6 text-[#F59E0B] mx-auto mb-2" />
            <div className="text-2xl font-['Outfit'] font-semibold">{stats.total_credits_earned}</div>
            <div className="text-xs text-[#A1A1AA]">Crediti guadagnati</div>
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="mt-6 pt-6 border-t border-white/10">
        <h4 className="font-['Outfit'] font-medium mb-3">Come funziona</h4>
        <ul className="space-y-2 text-sm text-[#A1A1AA]">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-[#0055FF]/20 text-[#0055FF] flex items-center justify-center flex-shrink-0 text-xs">1</span>
            Condividi il tuo codice o link con amici
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-[#0055FF]/20 text-[#0055FF] flex items-center justify-center flex-shrink-0 text-xs">2</span>
            I tuoi amici ottengono <strong className="text-white">20% di sconto</strong>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-[#0055FF]/20 text-[#0055FF] flex items-center justify-center flex-shrink-0 text-xs">3</span>
            Tu ricevi <strong className="text-[#F59E0B]">1.000 crediti AI</strong> per ogni acquisto
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ReferralSection;
